/**
 * Toast notifications shown in the bottom right corner.
 */

import { text } from "./i18n";
import { query, queryAll } from "./dom";
import type { ToastLevel } from "./types";

const STACK_ID = "flash-stack";
const AUTO_DISMISS_MS = 6000;

/** Return the container holding the toasts, creating it when missing. */
export function toastStack(): HTMLElement {
    const existing = query<HTMLElement>(`#${STACK_ID}`);
    if (existing) {
        return existing;
    }

    const created = document.createElement("div");
    created.className = "flash-stack";
    created.id = STACK_ID;
    created.setAttribute("role", "status");
    document.body.appendChild(created);

    return created;
}

/** Show a toast with the given message and severity. */
export function toast(message: string, level: ToastLevel = "success"): HTMLElement {
    const element = document.createElement("div");
    element.className = `flash flash--${level}`;

    const body = document.createElement("span");
    body.textContent = message;
    element.appendChild(body);

    const close = document.createElement("button");
    close.type = "button";
    close.setAttribute("data-dismiss-flash", "");
    close.setAttribute("aria-label", text("js.toast.close", "Zavrieť"));
    close.textContent = "×";
    element.appendChild(close);

    toastStack().appendChild(element);
    window.setTimeout(() => element.remove(), AUTO_DISMISS_MS);

    return element;
}

/** Wire the dismiss buttons and the automatic removal of server side flashes. */
export function initToasts(): void {
    const stack = query<HTMLElement>(`#${STACK_ID}`);
    if (!stack) {
        return;
    }

    stack.addEventListener("click", (event: Event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const button = target.closest<HTMLElement>("[data-dismiss-flash]");
        if (button) {
            button.parentElement?.remove();
        }
    });

    for (const flash of queryAll<HTMLElement>(".flash", stack)) {
        window.setTimeout(() => flash.remove(), AUTO_DISMISS_MS);
    }
}
